import { type StateCreator } from 'zustand';

import { type SaveAsSkillDraft } from '../../types/saveAsSkillDraft';

export type PendingSaveAsSkillSuggestion = { threadId: string; suggestionId: string };

/** Scope a created skill lands in; drives which tab "View skills" opens. */
export type SaveAsSkillSuccessScope = 'private' | 'workspace';

export type OnSaveAsSkill = (draft: SaveAsSkillDraft, suggestion?: PendingSaveAsSkillSuggestion | null) => void;

export type SaveAsSkillSlice = {
  onSaveAsSkill: OnSaveAsSkill | null;
  setOnSaveAsSkill: (fn: OnSaveAsSkill | null) => void;
  /** Dismissed suggestions keyed by thread ID, then by the triggering user message's ID. */
  dismissedByThread: Record<string, Record<string, boolean>>;
  dismissSuggestion: (threadId: string, suggestionId: string) => void;
  /** The suggestion whose Save modal is currently open, committed to dismissed only on save-success. */
  pendingSuggestion: PendingSaveAsSkillSuggestion | null;
  setPendingSuggestion: (pending: PendingSaveAsSkillSuggestion | null) => void;
  confirmPendingSuggestion: () => void;
  /** Set by the modal (which renders outside the canvas ToastProvider) for a canvas-mounted watcher to consume; null when none pending. */
  pendingSuccessToast: SaveAsSkillSuccessScope | null;
  triggerSuccessToast: (scope: SaveAsSkillSuccessScope) => void;
  clearSuccessToast: () => void;
};

export type CreateSaveAsSkillSlice = () => StateCreator<SaveAsSkillSlice, [], [], SaveAsSkillSlice>;

export const createSaveAsSkillSlice: CreateSaveAsSkillSlice = () => (set, get) => ({
  clearSuccessToast: () => {
    set({ pendingSuccessToast: null });
  },
  confirmPendingSuggestion: () => {
    const pending = get().pendingSuggestion;

    if (!pending) {
      return;
    }

    get().dismissSuggestion(pending.threadId, pending.suggestionId);
    set({ pendingSuggestion: null });
  },
  dismissedByThread: {},
  dismissSuggestion: (threadId, suggestionId) => {
    const existingThread = get().dismissedByThread[threadId] ?? {};

    if (existingThread[suggestionId]) {
      return;
    }

    set({
      dismissedByThread: {
        ...get().dismissedByThread,
        [threadId]: {
          ...existingThread,
          [suggestionId]: true,
        },
      },
    });
  },
  onSaveAsSkill: null,
  pendingSuccessToast: null,
  pendingSuggestion: null,
  setOnSaveAsSkill: (fn) => {
    set({ onSaveAsSkill: fn });
  },
  setPendingSuggestion: (pending) => {
    set({ pendingSuggestion: pending });
  },
  triggerSuccessToast: (scope) => {
    set({ pendingSuccessToast: scope });
  },
});
